import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SummarizeIcon from '@mui/icons-material/Summarize';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Grid, Paper, Skeleton, Typography } from '@mui/material';
import React from 'react';
import { ClaimStatus, ClaimType, EhrClaimsMetricsResponse } from '../../interfaces/billing';

interface ClaimsMetricsProps {
  metrics: EhrClaimsMetricsResponse['metrics'] | null;
  loading: boolean;
  selectedStatus?: string | null;
  onStatusClick?: (status: string | null) => void;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  [ClaimStatus.Pending]: {
    label: 'Pending',
    color: 'warning.main',
    icon: <HourglassEmptyIcon sx={{ fontSize: 28, color: 'warning.main' }} />,
  },
  [ClaimStatus.Uploaded]: {
    label: 'Uploaded',
    color: 'primary.main',
    icon: <UploadIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
  },
  [ClaimStatus.UploadedWithErrors]: {
    label: 'With Errors',
    color: 'error.main',
    icon: <ErrorIcon sx={{ fontSize: 28, color: 'error.main' }} />,
  },
  [ClaimStatus.Processed]: {
    label: 'Processed',
    color: 'success.main',
    icon: <CheckCircleIcon sx={{ fontSize: 28, color: 'success.main' }} />,
  },
};

const SOURCE_CONFIG: Record<string, { label: string }> = {
  [ClaimType.Appointment]: { label: 'Appointments' },
  [ClaimType.RPM]: { label: 'RPM' },
  [ClaimType.Unknown]: { label: 'Unknown' },
};

export const ClaimsMetrics: React.FC<ClaimsMetricsProps> = ({
  metrics,
  loading,
  selectedStatus,
  onStatusClick,
}) => {
  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={6} sm={2.4} key={i}>
            <Skeleton variant='rectangular' height={90} sx={{ borderRadius: 1 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!metrics) return null;

  const isClickable = !!onStatusClick;

  const handleClick = (status: string | null) => {
    if (!onStatusClick) return;
    // Toggle: click same status again to deselect
    onStatusClick(selectedStatus === status ? null : status);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        {/* Total */}
        <Grid item xs={6} sm={2}>
          <Paper
            variant='outlined'
            onClick={() => handleClick(null)}
            sx={{
              p: 1.5,
              textAlign: 'center',
              transition: 'all 0.2s',
              ...(isClickable && {
                cursor: 'pointer',
                '&:hover': { boxShadow: 2, bgcolor: 'action.hover' },
              }),
              ...(selectedStatus === null && isClickable && selectedStatus !== undefined && {
                borderColor: 'text.primary',
                borderWidth: 2,
              }),
            }}>
            <SummarizeIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
            <Typography variant='h5' fontWeight={600} color='text.primary'>
              {metrics.total}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Total Claims
            </Typography>
          </Paper>
        </Grid>

        {/* By Status */}
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const count = metrics.byStatus[status] || 0;
          const isSelected = selectedStatus === status;
          return (
            <Grid item xs={6} sm={2} key={status}>
              <Paper
                variant='outlined'
                onClick={() => count > 0 && handleClick(status)}
                sx={{
                  p: 1.5,
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  ...(isClickable && count > 0 && {
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 2, bgcolor: 'action.hover' },
                  }),
                  ...(isSelected && {
                    borderColor: config.color,
                    borderWidth: 2,
                    boxShadow: 3,
                  }),
                }}>
                {config.icon}
                <Typography variant='h5' fontWeight={600} color={config.color}>
                  {count}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {config.label}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* By Source - compact row */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        {Object.entries(SOURCE_CONFIG).map(([source, config]) => {
          const count = metrics.bySource[source] || 0;
          if (count === 0) return null;
          return (
            <Typography key={source} variant='body2' color='text.secondary'>
              {config.label}: <strong>{count}</strong>
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
};
